import React from "react";

import { css, cx } from "@emotion/css";
import Typography from "@mui/material/Typography";

import { useStyles } from "@/hooks/useStyles";
import { OnData, OnError, OnRawData } from "@/lib/drivers/DeviceDriver";
import { Theme } from "@/theme/CreateTheme.ts";
import { Card } from "@/uiKit/atoms/card/Card.tsx";
import { CardContent } from "@/uiKit/atoms/card/CardContent.tsx";
import { CardFooter } from "@/uiKit/atoms/card/CardFooter.tsx";
import { CardHeader } from "@/uiKit/atoms/card/CardHeader.tsx";
import { Progress, ProgressListener } from "@/utils/ProgressMixin";

export type BaseDeviceCardProps = {
  onRawData: OnRawData;
  onData: OnData;
  onError: OnError;
  progress: Progress | undefined;
  progressListener: ProgressListener;
};

export type DeviceCardProps = {
  deviceName: string;
  deviceInstructions: string;
  action: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  classes?: {
    root?: string;
    header?: string;
    content?: string;
  };
};

export const DeviceCard: React.FC<DeviceCardProps> = ({
  deviceName,
  deviceInstructions,
  action,
  children,
  className,
  classes,
}) => {
  const styles = useStyles(makeStyles);

  return (
    <Card className={cx(className, classes?.root)}>
      <CardHeader
        title={
          <div className={cx(styles.header, classes?.header)}>
            <Typography variant="h6">{deviceName}</Typography>
            <Typography variant="body">{deviceInstructions}</Typography>
          </div>
        }
        action={action}
        className={classes?.header}
      />
      <CardContent className={cx(styles.content, classes?.content)}>{children}</CardContent>
      <CardFooter />
    </Card>
  );
};

const makeStyles = (theme: Theme) => ({
  content: css`
    display: flex;
    flex-direction: row;
    align-items: start;
    gap: ${theme.spacing(16)};
  `,
  header: css({
    display: "flex",
    flexDirection: "column",
  }),
});
